import { Trans, useLingui } from '@lingui/react/macro'
import { Link } from '@tanstack/react-router'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { X } from 'lucide-react'
import type { ReactElement } from 'react'

import {
  LotoEditorLinkSlot,
  LotoPanelHeader,
  LotoPanelHeading,
  LotoPanelTitle,
} from './lotoPagePanel.styles'

type LotoPagePanelHeaderProps = {
  documentName: string
  pageNumber: number
  reportId: string | undefined
  onClose: () => void
}

function FullReportLink({ reportId }: { reportId: string }): ReactElement {
  return (
    <LotoEditorLinkSlot>
      <Button asChild variant='ghost' size='sm'>
        <Link to='/loto-reports/$reportId' params={{ reportId }}>
          <Trans>View full report</Trans>
        </Link>
      </Button>
    </LotoEditorLinkSlot>
  )
}

function PageTitle({
  documentName,
  pageNumber,
}: Pick<
  LotoPagePanelHeaderProps,
  'documentName' | 'pageNumber'
>): ReactElement {
  const { t } = useLingui()
  if (!documentName)
    return (
      <Text as='span' variant='caption' color='muted' wrap='nowrap'>
        {t`Page ${pageNumber}`}
      </Text>
    )
  return (
    <LotoPanelTitle title={t`${documentName} · page ${pageNumber}`}>
      <Text as='span' variant='caption' color='muted' truncate>
        {documentName}
      </Text>
      <Text as='span' variant='caption' color='muted' wrap='nowrap'>
        {t({ message: `· page ${pageNumber}`, context: 'LOTO panel title' })}
      </Text>
    </LotoPanelTitle>
  )
}

export function LotoPagePanelHeader({
  documentName,
  pageNumber,
  reportId,
  onClose,
}: LotoPagePanelHeaderProps): ReactElement {
  const { t } = useLingui()
  const closeLabel = t`Close LOTO panel`
  return (
    <LotoPanelHeader>
      <LotoPanelHeading>
        <Text as='span' variant='overline' color='primary'>
          {t`LOTO`}
        </Text>
        <PageTitle documentName={documentName} pageNumber={pageNumber} />
      </LotoPanelHeading>
      {reportId && <FullReportLink reportId={reportId} />}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type='button'
            variant='ghost'
            size='icon-sm'
            aria-label={closeLabel}
            onClick={onClose}>
            <X />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{closeLabel}</TooltipContent>
      </Tooltip>
    </LotoPanelHeader>
  )
}
